import { useEffect, useState } from 'react';
import {
  ActionList,
  ActionListItem,
  Alert,
  Badge,
  BottomNavigation,
  Button,
  Checkbox,
  Chip,
  ChipGroup,
  ContentSkeleton,
  Divider,
  EmptyState,
  Eyebrow,
  FileUploadList,
  FormActions,
  FormSection,
  IconButton,
  LinkButton,
  MediaControlButton,
  MetadataRow,
  MiniappPage,
  PageContent,
  ProgressSlider,
  RadioCard,
  SearchField,
  SectionHeader,
  SegmentedControl,
  Select,
  StatusPanel,
  Surface,
  TagList,
  Textarea,
  TextInput,
  Toggle,
  TopAppBar,
} from '../components';

type PreviewTheme = 'dark' | 'light';
type PreviewWidth = '320' | '430';
type PreviewSegment = 'one' | 'two';

const longLabel = 'Дети и внутренний ребёнок, отношения с собой и близкими людьми';

export default function UiKitPreview() {
  const [theme, setTheme] = useState<PreviewTheme>('dark');
  const [width, setWidth] = useState<PreviewWidth>('320');
  const [search, setSearch] = useState('Практика');
  const [segment, setSegment] = useState<PreviewSegment>('one');
  const [checked, setChecked] = useState(true);
  const [radio, setRadio] = useState('recommended');
  const [progress, setProgress] = useState(38);

  useEffect(() => {
    const root = document.documentElement;
    const previousThemeDark = root.classList.contains('theme-dark');
    const previousThemeLight = root.classList.contains('theme-light');

    root.classList.toggle('theme-dark', theme === 'dark');
    root.classList.toggle('theme-light', theme === 'light');

    return () => {
      root.classList.toggle('theme-dark', previousThemeDark);
      root.classList.toggle('theme-light', previousThemeLight);
    };
  }, [theme]);

  return (
    <MiniappPage>
      <PageContent className="gap-8">
        <header className="grid gap-3">
          <Eyebrow>UI Kit, Step 1</Eyebrow>
          <h1 className="m-0 font-headline text-4xl leading-none text-on-background">
            Спокойный редакционный проводник
          </h1>
          <p className="m-0 max-w-[65ch] text-sm leading-6 text-text-secondary">
            Проверка общих компонентов на длинных русских подписях, интерактивных состояниях и узких
            экранах Telegram.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <SegmentedControl
              label="Тема"
              value={theme}
              onChange={setTheme}
              items={[
                { value: 'dark', label: 'Тёмная' },
                { value: 'light', label: 'Светлая' },
              ]}
            />
            <SegmentedControl
              label="Ширина предпросмотра"
              value={width}
              onChange={setWidth}
              items={[
                { value: '320', label: '320 px' },
                { value: '430', label: '430 px' },
              ]}
            />
          </div>
        </header>

        <div
          className="mx-auto grid w-full min-w-0 gap-8 overflow-hidden rounded-2xl border border-outline-variant bg-background shadow-floating"
          style={{ width: `${width}px`, maxWidth: '100%' }}
        >
          <TopAppBar
            title="Практики и материалы"
            onBack={() => undefined}
            actions={
              <IconButton label="Дополнительные действия" variant="ghost">
                <span className="material-symbols-outlined" aria-hidden="true">
                  more_vert
                </span>
              </IconButton>
            }
            fixed={false}
          />

          <div className="grid min-w-0 gap-8 px-4 pb-4">
            <section className="grid min-w-0 gap-4">
              <SectionHeader
                eyebrow="Действия"
                title="Кнопки и ссылки"
                description="Восемь обязательных состояний строятся на одной форме и семантических цветах."
              />
              <div className="grid grid-cols-2 gap-2">
                <Button>Продолжить</Button>
                <Button variant="secondary">Позже</Button>
                <Button variant="ghost">Подробнее</Button>
                <Button variant="danger">Удалить</Button>
                <Button loading>Сохраняем</Button>
                <Button disabled>Недоступно</Button>
              </div>
              <LinkButton href="#forms" fullWidth iconPosition="right" icon="↗">
                Открыть подробное описание формата консультации
              </LinkButton>
            </section>

            <Divider />

            <section id="forms" className="grid min-w-0 gap-4">
              <SectionHeader
                eyebrow="Формы"
                title="Поля и выбор"
                description="Нативный select остаётся устойчивым внутри Telegram WebView."
              />
              <SearchField
                label="Поиск по практикам"
                placeholder="Поиск по практикам"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onClear={() => setSearch('')}
              />
              <TextInput
                label="Название"
                placeholder="Введите название"
                defaultValue="Разговор о внутренней опоре"
              />
              <TextInput
                label="Ссылка с длинным адресом"
                defaultValue="https://example.com/very-long-path-that-must-not-expand-the-mobile-viewport"
                error="Проверьте адрес ссылки"
              />
              <Select label="Доступ" defaultValue="recommended">
                <option value="all">Любой доступ</option>
                <option value="recommended">Рекомендуемые материалы</option>
              </Select>
              <Textarea
                label="Описание"
                hint="Текст переносится и сохраняет удобную длину строки."
                defaultValue="Спокойное описание, которое занимает несколько строк и не ломает геометрию узкого экрана."
              />
              <Checkbox
                label="Показывать только доступные сейчас материалы"
                checked={checked}
                onChange={(event) => setChecked(event.target.checked)}
              />
              <Toggle
                label="Получать уведомления о новых материалах"
                checked={checked}
                onChange={setChecked}
              />
              <div className="grid gap-2">
                <RadioCard
                  name="preview-radio"
                  value="recommended"
                  checked={radio === 'recommended'}
                  onChange={setRadio}
                  title="Рекомендуемые"
                  description="Материалы, которые лучше подходят к выбранному состоянию."
                />
                <RadioCard
                  name="preview-radio"
                  value="all"
                  checked={radio === 'all'}
                  onChange={setRadio}
                  title="Все материалы"
                />
              </div>
            </section>

            <Divider />

            <section className="grid min-w-0 gap-4">
              <SectionHeader
                eyebrow="Фильтры"
                title="Чипы и сегменты"
                description="Таксономия переносится на новую строку, короткие типы могут жить в ограниченной горизонтальной ленте."
              />
              <SegmentedControl
                label="Тип результата"
                value={segment}
                onChange={setSegment}
                items={[
                  { value: 'one', label: 'Практики' },
                  { value: 'two', label: 'Эфиры' },
                ]}
              />
              <ChipGroup label="Темы" layout="wrap">
                <Chip selected>Все</Chip>
                <Chip>Медитация</Chip>
                <Chip tone="taxonomy" selected>
                  {longLabel}
                </Chip>
                <Chip tone="taxonomy">Родители</Chip>
              </ChipGroup>
              <ChipGroup label="Короткая лента типов" layout="rail">
                <Chip>Все материалы</Chip>
                <Chip selected>Практики</Chip>
                <Chip>Подкасты</Chip>
                <Chip>Прямые эфиры</Chip>
              </ChipGroup>
            </section>

            <Divider />

            <section className="grid min-w-0 gap-4">
              <SectionHeader eyebrow="Состояния" title="Обратная связь" />
              <Alert type="success" message="Настройки сохранены. Изменения уже применены." />
              <Alert
                type="error"
                message="Не удалось загрузить материалы. Проверьте подключение и попробуйте снова."
              />
              <StatusPanel
                tone="warning"
                title="Доступ ограничен"
                description="Этот материал откроется после подтверждения доступа."
                action={{ label: 'Узнать подробнее', onClick: () => undefined }}
                compact
              />
              <EmptyState
                title="Пока ничего не найдено"
                message="Измените тему или состояние, чтобы увидеть другие материалы."
                action={{ label: 'Сбросить фильтры', onClick: () => undefined }}
              />
              <ContentSkeleton rows={2} />
            </section>

            <Divider />

            <section className="grid min-w-0 gap-4">
              <SectionHeader eyebrow="Контент" title="Списки и метаданные" />
              <ActionList label="Действия с материалом">
                <ActionListItem
                  leading={
                    <span className="material-symbols-outlined" aria-hidden="true">
                      play_circle
                    </span>
                  }
                  title="Продолжить прослушивание"
                  description="Осталось 18 минут"
                />
                <ActionListItem
                  leading={
                    <span className="material-symbols-outlined" aria-hidden="true">
                      bookmark
                    </span>
                  }
                  title={longLabel}
                  description="Очень длинная подпись переносится внутри доступной ширины."
                />
              </ActionList>
              <Surface tone="raised" className="grid gap-3">
                <div className="flex min-w-0 flex-wrap gap-2">
                  <Badge label="Новое" variant="accent" />
                  <Badge label="Практика" variant="taxonomy" />
                  <Badge label="Доступно" variant="success" />
                </div>
                <MetadataRow
                  items={[
                    { label: 'Продолжительность', value: '24 мин' },
                    { label: 'Дата', value: '23 июля' },
                  ]}
                />
                <TagList
                  label="Связанные темы"
                  tone="taxonomy"
                  tags={['Тревога', 'Внутренняя опора', 'Отношения']}
                />
              </Surface>
              <FileUploadList
                files={[
                  {
                    id: 'one',
                    name: 'очень-длинное-название-аудиозаписи-практики-для-публикации.mp3',
                    meta: '18,4 МБ',
                  },
                  {
                    id: 'two',
                    name: 'обложка.webp',
                    meta: 'Ошибка загрузки',
                    status: 'error',
                  },
                ]}
                onRemove={() => undefined}
              />
            </section>

            <Divider />

            <FormSection
              title="Действия формы"
              description="Основное действие остаётся последним в визуальном порядке на мобильном."
            >
              <FormActions
                primaryLabel="Сохранить изменения"
                secondaryLabel="Отменить"
                destructiveLabel="Удалить запись"
              />
            </FormSection>

            <Divider />

            <section className="grid min-w-0 gap-4">
              <SectionHeader eyebrow="Медиа" title="Управление воспроизведением" />
              <Surface tone="raised" className="grid gap-4">
                <ProgressSlider
                  label="Позиция воспроизведения"
                  value={progress}
                  onChange={(event) => setProgress(Number(event.target.value))}
                  bufferedPercent={68}
                />
                <div className="flex items-center justify-center gap-4">
                  <MediaControlButton
                    label="Назад на 15 секунд"
                    icon={
                      <span className="material-symbols-outlined" aria-hidden="true">
                        replay_10
                      </span>
                    }
                    onClick={() => undefined}
                  />
                  <MediaControlButton
                    label="Воспроизвести"
                    tone="primary"
                    size="lg"
                    icon={
                      <span className="material-symbols-outlined text-3xl" aria-hidden="true">
                        play_arrow
                      </span>
                    }
                    onClick={() => undefined}
                  />
                  <MediaControlButton
                    label="Вперёд на 15 секунд"
                    icon={
                      <span className="material-symbols-outlined" aria-hidden="true">
                        forward_10
                      </span>
                    }
                    onClick={() => undefined}
                  />
                </div>
              </Surface>
            </section>
          </div>

          <BottomNavigation
            fixed={false}
            activeId="materials"
            items={[
              {
                id: 'home',
                label: 'Главная',
                icon: <span className="material-symbols-outlined">home</span>,
              },
              {
                id: 'materials',
                label: 'Материалы',
                icon: <span className="material-symbols-outlined">library_books</span>,
              },
              {
                id: 'events',
                label: 'События',
                icon: <span className="material-symbols-outlined">event</span>,
              },
              {
                id: 'navigator',
                label: 'Навигатор',
                icon: <span className="material-symbols-outlined">near_me</span>,
              },
            ]}
          />
        </div>
      </PageContent>
    </MiniappPage>
  );
}
